import { AxiosInstance } from "axios";
import useSWR from "swr";

import { errorFormatter } from 'services/serviceUtils/responseFormatter';
import { Error } from 'services/types/CommonTypes';

import { SubmissionResponse } from "services/types/TaskTypes";
import { fetcherWithToken } from "./ApiClient";

export const useTask = (taskId: string, token: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    !!taskId ? [`${process.env.REACT_APP_DOMAIN}/api/tasks/${taskId}/`, token] : null,
    ([url, token]) => fetcherWithToken(url, token));

  return {
    data: data || {},
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  }
};

// Hook for fetching tasks with authentication (for private campaigns)
export const useTasksAuthenticated = (campaignId: string, token: string, enabled = true, sortBy = 'task_order') => {
  const { data, error, isLoading, mutate } = useSWR(
    (!!campaignId && !!token && enabled) ? [`${process.env.REACT_APP_DOMAIN}/api/tasks/?campaign=${campaignId}&sort=${sortBy}`, token] : null,
    ([url, token]) => fetcherWithToken(url, token));

  return {
    data: data?.results || [],
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  }
};

export const useSubmissions = (taskId: string, token: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    !!taskId ? [`${process.env.REACT_APP_DOMAIN}/api/submissions/?task=${taskId}&sort=-is_pinned,-created&is_active=true`, token] : null,
    ([url, token]) => fetcherWithToken(url, token));

  return {
    data: data?.results || [],
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  }
};

export const useCampaignSubmissions = (userId: string, campaignId: string, token: string) => {
  const shouldFetch = userId && campaignId && token;

  const queryParams = new URLSearchParams({
    user: userId.toLowerCase(),
    'task__campaign': campaignId.toLowerCase(),
    sort: '-created',
    is_with_images: 'true',
    is_active: 'true',
    'page[size]': '9'
  });

  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? [`${process.env.REACT_APP_DOMAIN}/api/submissions/?${queryParams.toString()}`, token] : null,
    ([url, token]) => fetcherWithToken(url, token)
  );

  return {
    data: data?.results || [],
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  };
};

export interface TaskProps {
  submitTaskAnswers: (data: { taskId: string; message: string; images?: string[]; }) => Promise<SubmissionResponse>;
  likeSubmission: (submissionId: string) => Promise<{ success: boolean; error?: Error }>;
  unlikeSubmission: (submissionId: string) => Promise<{ success: boolean; error?: Error }>;
}

function createTaskService(axiosClient: AxiosInstance): TaskProps {
  return {
    submitTaskAnswers: async ({ taskId, message, images }): Promise<SubmissionResponse> => {
      return axiosClient.post('/api/submissions/', {
        task: `${process.env.REACT_APP_DOMAIN}/api/tasks/${taskId}/`,
        message,
        images,
      }).then(
        (res) => res?.data,
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { error: formattedError };
        }
      );
    },

    likeSubmission: async (submissionId: string): Promise<{ success: boolean; error?: Error }> => {
      return axiosClient.post(`/api/submissions/${submissionId}/like/`).then(
        (res) => ({ success: true }),
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { success: false, error: formattedError };
        }
      );
    },

    unlikeSubmission: async (submissionId: string): Promise<{ success: boolean; error?: Error }> => {
      return axiosClient.delete(`/api/submissions/${submissionId}/like/`).then(
        (res) => ({ success: true }),
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { success: false, error: formattedError };
        }
      );
    },
  };
}

export default createTaskService;